// The lab's aligner pricing (threshold + two per-tray rates), shared by every screen that shows a
// clinic price next to a tray count. Fetched once per app load from /settings/aligner-pricing,
// cached at module level so pages don't each round-trip, and refreshed in place when an admin
// saves new pricing (the pricing.changed live event carries the new values). Until the first
// fetch resolves the built-in defaults are used, so a price is always shown.
import { useEffect, useState } from 'react';
import { ALIGNER_PRICING, type AlignerPricing } from '@alignova/shared';
import { api } from './api.ts';
import { useLiveEvent } from './useLive.ts';

let cached: AlignerPricing = { ...ALIGNER_PRICING };
let inflight: Promise<void> | null = null;
const listeners = new Set<(p: AlignerPricing) => void>();

function setCached(next: AlignerPricing): void {
  cached = next;
  for (const cb of listeners) cb(next);
}

function isPricing(v: unknown): v is AlignerPricing {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return ['threshold', 'rateAtOrBelow', 'rateAbove'].every((k) => typeof o[k] === 'number' && Number.isFinite(o[k]));
}

function load(): Promise<void> {
  if (!inflight) {
    inflight = api
      .get<AlignerPricing>('/settings/aligner-pricing')
      .then((p) => {
        if (isPricing(p)) setCached(p);
      })
      .catch(() => undefined) // keep whatever we have; defaults are better than nothing
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Current pricing (defaults until loaded); re-renders when it changes. */
export function useAlignerPricing(): AlignerPricing {
  const [pricing, setPricing] = useState<AlignerPricing>(cached);
  useEffect(() => {
    listeners.add(setPricing);
    setPricing(cached);
    void load();
    return () => {
      listeners.delete(setPricing);
    };
  }, []);
  useLiveEvent<unknown>('pricing.changed', (payload) => {
    if (isPricing(payload)) setCached(payload);
    else void load();
  });
  return pricing;
}
